import { detectDevice } from '../core/device';

/**
 * Portrait orientation warning overlay.
 * Shows a fullscreen overlay prompting the user to rotate their device to landscape.
 * Uses CSS media queries to auto-show/hide based on orientation.
 *
 * BUG-HUD-6 / BUG-UI-7: The overlay only activates while gameplay is in
 * progress (body has `.in-game` class). Pre-game screens (auth, registration,
 * server browser, matchmaking, team select) remain reachable in portrait.
 * Tablets never see the prompt — their form factor handles portrait fine.
 */
export class RotateOverlay {
  private overlay: HTMLElement | null = null;
  private styleEl: HTMLStyleElement | null = null;

  /**
   * Initialize the overlay and inject the media-query style tag.
   * Call once at startup. No-op on tablets (BUG-HUD-6).
   */
  init(): void {
    // Tablets are wide enough to play in portrait — never show the rotate prompt.
    if (detectDevice() === 'tablet') return;

    // Create the overlay element
    this.overlay = document.createElement('div');
    this.overlay.id = 'rotate-overlay';
    this.overlay.innerHTML = `
      <div class="rotate-overlay-content">
        <div class="rotate-overlay-icon" aria-hidden="true">
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="18" y="10" width="30" height="50" rx="4" stroke="white" stroke-width="3" fill="none">
              <animateTransform attributeName="transform" type="rotate" from="0 33 35" to="-90 33 35" dur="1.5s" repeatCount="indefinite" />
            </rect>
            <path d="M56 50 C62 50, 66 46, 66 40 L66 30" stroke="white" stroke-width="2.5" fill="none" stroke-linecap="round"/>
            <polyline points="62,32 66,26 70,32" stroke="white" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <p class="rotate-overlay-text">Rotate your device to play</p>
        <p class="rotate-overlay-subtext">This game requires landscape orientation</p>
      </div>
    `;
    document.body.appendChild(this.overlay);

    // Inject style tag with media query
    this.styleEl = document.createElement('style');
    this.styleEl.id = 'rotate-overlay-styles';
    this.styleEl.textContent = `
      #rotate-overlay {
        display: none;
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.95);
        z-index: 9999;
        justify-content: center;
        align-items: center;
      }

      .rotate-overlay-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
        text-align: center;
        padding: 20px;
      }

      .rotate-overlay-icon {
        opacity: 0.9;
      }

      .rotate-overlay-text {
        color: #ffffff;
        font-size: 22px;
        font-family: 'Courier New', monospace;
        font-weight: bold;
        letter-spacing: 2px;
        text-transform: uppercase;
        margin: 0;
      }

      .rotate-overlay-subtext {
        color: #888888;
        font-size: 14px;
        font-family: 'Courier New', monospace;
        letter-spacing: 1px;
        margin: 0;
      }

      @media (orientation: portrait) {
        /* BUG-UI-7: gate the rotate prompt on body.in-game so pre-game screens
           (auth, registration, server browser, matchmaking, team select)
           remain usable in portrait. Call setInGame(true) when gameplay
           starts to enable the overlay. */
        body.in-game #rotate-overlay {
          display: flex;
        }

        body.in-game #game-canvas,
        body.in-game #hud,
        body.in-game #main-menu {
          visibility: hidden;
        }

        /* MOB-ERG: in portrait the rotate-prompt covers gameplay, but the touch
           overlays sit at z-index 1001 above #hud. Force them inert so taps
           inside the rotate-prompt don't register as fire/joystick events. */
        body.in-game #touch-buttons-overlay,
        body.in-game #touch-joystick-zone,
        body.in-game #hud-layout-editor {
          pointer-events: none !important;
          visibility: hidden;
        }
      }
    `;
    document.head.appendChild(this.styleEl);
  }

  /**
   * Toggle the in-game state. The rotate-prompt CSS only activates while
   * `body.in-game` is set, so pre-game screens stay usable in portrait.
   * Downstream callers should invoke this when the match starts (true) and
   * when returning to the main menu / lobby (false).
   */
  setInGame(active: boolean): void {
    document.body.classList.toggle('in-game', active);
  }

  /**
   * Remove the overlay and style tag from the DOM.
   */
  dispose(): void {
    document.body.classList.remove('in-game');
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
    if (this.styleEl) {
      this.styleEl.remove();
      this.styleEl = null;
    }
  }
}
